import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../images/logo.png";
import "../importData/Home.css";
import API_BASE from "../config/api";

// for firebase
import {
  signInWithPhoneNumber,
  onAuthStateChanged,
} from "firebase/auth";
import { auth } from "../config/config-firebase";
import { message } from "antd"; // Using antd message for cleaner errors
import {
  getRecaptchaVerifier,
  clearRecaptchaVerifier,
} from "../utils/recaptcha";
import { clearStaleAdminMerchantModeFlag } from "../utils/adminMerchantMode";
import { MSA_PATH } from "../constants/msa";

const countryCodes = [
  { code: "US", dial_code: "+1" },
  { code: "MX", dial_code: "+52" },
  { code: "HK", dial_code: "+852" }, // for remi-testing
  { code: "IN", dial_code: "+91" },
];

const RegistrationScreen = () => {
  const [businessName, setBusinessName] = useState("");
  const [businessAddress, setBusinessAddress] = useState("");
  const [businessCity, setBusinessCity] = useState("");
  const [businessState, setBusinessState] = useState("");
  const [businessCountry, setBusinessCountry] = useState("");
  const [businessZip, setBusinessZip] = useState("");
  const [EINnumber, setEINnumber] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [routingNumber, setRoutingNumber] = useState("");
  const [username, setUsername] = useState("");
  const [isSmsEnabled, setIsSmsEnabled] = useState(false);
  const [agreedToMSA, setAgreedToMSA] = useState(false);

  // const [businessEmail, setBusinessEmail] = useState('');
  // const [businessPhoneNumber, setBusinessPhoneNumber] = useState('');
  const [businessUsername, setBusinessUsername] = useState("");
  const [password, setPassword] = useState("");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [zip, setZip] = useState("");
  const [ssn, setSsn] = useState("");

  const navigate = useNavigate();

  const [questionSet, setQuestionSet] = useState(1);

  const [user, setUser] = useState(null);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);

  // new states for handling phone auth
  const [verificationCode, setVerificationCode] = useState("");
  const [confirmObj, setConfirmObj] = useState(null);
  const [codeSent, setCodeSent] = useState(false);
  const [error, setError] = useState("");
  const [isRequestButtonDisabled, setIsRequestButtonDisabled] = useState(false);
  const [isVerifyButtonDisabled, setIsVerifyButtonDisabled] = useState(false);

  const [selectedCountryCode, setSelectedCountryCode] = useState(
    countryCodes[0].dial_code,
  );

  const combinedPhoneNumber = selectedCountryCode + phoneNumber;

  const isPhoneNumberValid = () => {
    // Check length greater than a minimum and matches basic phone pattern
    const minLength = 8;
    const pattern = /^\+[0-9\s\-\(\)]+$/;
    return (
      combinedPhoneNumber.length > minLength &&
      pattern.test(combinedPhoneNumber)
    );
  };

  const handleCountryCodeChange = (event) => {
    setSelectedCountryCode(event.target.value);
  };

  const handlePhoneNumberChange = (event) => {
    setPhoneNumber(event.target.value);
  };

  useEffect(() => {
    const clearAuth = async () => {
      try {
        await auth.signOut();
        console.log("Cleared old Firebase session");
      } catch (e) {
        console.log("Signout failed but continuing...");
      }
    };

    clearAuth();
  }, []);

  const handleCheckboxChange = (event) => {
    setIsSmsEnabled(event.target.checked);
  };

  useEffect(() => {
    // Track Firebase auth state only — do NOT navigate here.
    // Phone verification (confirmObj.confirm()) flips this listener the
    // instant it succeeds, which is BEFORE submitRegistrationData has
    // called /registerMerchant and BEFORE handleRegistrationSuccess has
    // saved customer_id to localStorage. A navigate("/home") here used to
    // race that flow and could render a protected route (/home or
    // /choose-plan) before customer_id existed, bouncing the brand-new
    // user to /login instead. handleRegistrationSuccess is the single
    // source of truth for where to go after registration (choose-plan vs
    // home), so navigation only happens there.
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });

    // Cleanup subscription on component unmount
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    console.log("username", username);
  }, [username]);

  // --- API Calls (Moved to top for clarity) ---

  const CHECK_USERNAME_URL = `${API_BASE}/checkUsernameExist`;

  const checkUsernameAvailability = async (name) => {
    if (!name) return false;
    try {
      const formdata = { username: name };
      const response = await fetch(CHECK_USERNAME_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formdata),
      });

      const data = await response.json();
      console.log("Username Check Response:", data);
      if (data.exists) {
        message.warning("Username already exists!"); // Use message.warning instead of alert
      }
      return data.exists; // true = exists, false = available
    } catch (error) {
      console.error("Error checking username:", error);
      message.error(
        "Error checking username availability. Treating as unavailable.",
      );
      return true; // treat as exists → safer
    }
  };

  const handleRegistrationSuccess = useCallback((customerId) => {
    if (customerId) {
      localStorage.setItem("customer_id", customerId);
      // Real merchant account — don't inherit a stale Admin Merchant Mode
      // flag from a previous impersonation session on this browser.
      clearStaleAdminMerchantModeFlag();
    }
    // A brand-new registration never has an existing subscription — there's
    // no backend endpoint to check that (getSubscriptionStatus doesn't
    // exist on this API), so just send every new signup to Choose Plan.
    navigate("/choose-plan", {
      state: { customerId },
    });
  }, [navigate]);

  const API_URL = `${API_BASE}/registerMerchant`;
  const submitRegistrationData = useCallback(
    async (token) => {
      // useCallback for dependency handling

      const registrationData = {
        token,
        businessName,
        businessAddress,
        businessCity: null,
        businessState: null,
        businessCountry: null,
        businessZip: 0,
        EINnumber: null,
        bankName: null,
        accountNumber: null,
        routingNumber: null,
        businessEmail: null,
        businessUsername: null,
        firstName,
        lastName,
        email,
        phoneNumber: combinedPhoneNumber,
        address: null,
        city: null,
        state: null,
        country: null,
        zip: 0,
        ssn: null,
        postcode: 0,
        username,
        password,
        smsOptIn: isSmsEnabled,
        msaOptIn: agreedToMSA,
      };

      try {
        // ✅ Set flag BEFORE the API call so Home.js knows to wait
        localStorage.setItem("registration_in_progress", "true");
        const response = await fetch(API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(registrationData),
        });

        const responseData = await response.json();
        console.log("Registration Response:", responseData);

        if (responseData.success === true) {
          console.log("regsiter success");
          // ✅ Clear the flag only after success, then navigate
          localStorage.removeItem("registration_in_progress");
          message.success("Registration successful!");
          const customerId = responseData.data?.customer_id;
          await handleRegistrationSuccess(customerId);
        } else {
          // ✅ Clear flag on failure too
          localStorage.removeItem("registration_in_progress");
          console.log("not register");
          // message.error(`Registration failed: ${responseData.message || 'Server error'}`);
        }
      } catch (error) {
        localStorage.removeItem("registration_in_progress"); // ✅ Always clean up

        console.error("Error submitting registration data:", error);
        message.error("Network error during final registration.");
      }
    },
    [
      businessName,
      businessAddress,
      firstName,
      lastName,
      email,
      combinedPhoneNumber,
      username,
      password,
      isSmsEnabled,
      agreedToMSA,
      navigate,
      handleRegistrationSuccess,
    ],
  ); // Added necessary dependencies

  // --- Phone Auth Logic ---

  const sendVerificationCode = useCallback(async () => {
    if (isSigningIn) return;

    // --- Validation Checks ---
    if (!username || !password || !firstName || !lastName || !email) {
      message.warning(
        "Please complete all required fields (Name, Email, Username, Password).",
      );
      return;
    }

    const exists = await checkUsernameAvailability(username);

    if (exists === true) {
      // message.warning("Username already exist"); // Handled inside checkUsernameAvailability
      return; // ⛔ STOP
    }

    if (!isPhoneNumberValid()) {
      message.error("Please enter a valid phone number.");
      return;
    }

    if (!agreedToMSA) {
      setError(
        "You must agree to the Master Service Agreement to continue.",
      );
      message.error(
        "You must agree to the Master Service Agreement to continue.",
      );
      return;
    }
    // --- End Validation Checks ---

    setIsRequestButtonDisabled(true); // Disable button after first click

    // 1. Initialize reCAPTCHA
    try {
      getRecaptchaVerifier("recaptcha-container");
    } catch (e) {
      console.error("Recaptcha Initialization Error:", e);
      message.error(
        "Could not initialize security verification. Please try refreshing.",
      );
      setIsRequestButtonDisabled(false);
      return;
    }

    // 2. Send SMS
    setIsSigningIn(true);
    try {
      // Ensure the verifier exists before calling Firebase
      if (!window.recaptchaVerifier) {
        throw new Error("Recaptcha object not found after initialization.");
      }

      const confirmationResult = await signInWithPhoneNumber(
        auth,
        combinedPhoneNumber,
        window.recaptchaVerifier,
      );

      setConfirmObj(confirmationResult);
      setCodeSent(true);
      message.success(`Verification code sent to ${combinedPhoneNumber}.`);
    } catch (error) {
      console.error("Error during signInWithPhoneNumber", error);
      // FIXED: Use antd message instead of alert
      message.error(
        `Failed to send verification code: ${error.message}. Please try again.`,
      );

      // Clear reCAPTCHA on failure so the next attempt gets a clean container
      clearRecaptchaVerifier("recaptcha-container");

      setIsRequestButtonDisabled(false); // Re-enable if sending fails
    } finally {
      setIsSigningIn(false);
    }
  }, [
    combinedPhoneNumber,
    username,
    password,
    firstName,
    lastName,
    email,
    isSigningIn,
    agreedToMSA,
  ]); // Added necessary dependencies

  const verifyCodeAndCreateAccount = useCallback(async () => {
    if (!confirmObj) {
      message.error(
        "Verification process not initialized. Please request a new code.",
      );
      return;
    }
    if (!verificationCode) {
      message.warning("Please enter the verification code.");
      return;
    }

    setIsVerifyingCode(true);
    setIsVerifyButtonDisabled(true);

    try {
      const result = await confirmObj.confirm(verificationCode);
      setIsVerifyingCode(false);

      const user = result.user;
      // Match Login.js: persist verified E.164 phone so protected screens (e.g. /ai-menu) work on first register
      localStorage.setItem("phone", combinedPhoneNumber);
      const token = await user.getIdToken();

      // Submit is now defined using useCallback and includes dependencies
      await submitRegistrationData(token);
    } catch (error) {
      console.error("Error while checking the verification code", error);
      setIsVerifyingCode(false);
      // FIXED: Use antd message instead of alert
      message.error(
        `Invalid verification code. Please try again. Error: ${error.message}`,
      );
      setError("Invalid verification code. Please try again.");
      setVerificationCode("");
      setIsVerifyButtonDisabled(false);

      // FIXED: Ensure reset is called correctly and defensively
      if (
        window.recaptchaVerifier &&
        typeof window.recaptchaVerifier.reset === "function"
      ) {
        window.recaptchaVerifier.reset();
      }
    } finally {
      setIsVerifyingCode(false);
    }
  }, [verificationCode, confirmObj, combinedPhoneNumber, submitRegistrationData]); // Added submitRegistrationData to dependencies

  useEffect(() => {
    clearRecaptchaVerifier("recaptcha-container");
    return () => {
      clearRecaptchaVerifier("recaptcha-container");
    };
  }, []);

  useEffect(() => {
    console.log("username", username);
  }, [username]);

  // Handle the next button click
  const handleNextClick = (event) => {
    event.preventDefault();

    // Custom validation for step 1
    if (questionSet === 1) {
      if (!businessName || !businessAddress) {
        message.warning(
          "Please fill in both Business Name and Business Address.",
        );
        return;
      }
    }

    // Check if it's the step for code verification (Step 2)
    if (questionSet === 2) {
      // Validation check for Step 2 fields
      if (
        !username ||
        !password ||
        !firstName ||
        !lastName ||
        !email ||
        !isPhoneNumberValid()
      ) {
        message.warning(
          "Please complete all required contact fields and ensure the phone number is valid.",
        );
        return;
      }

      if (codeSent) {
        // If code is sent, do nothing or allow manual verification, but don't move to a non-existent step
        // This button's function is now replaced by Request/Verify buttons below.
        return;
      } else {
        // If the code has not been sent, attempt to send it
        // We can remove this branch since the button below handles the submit action
        // sendVerificationCode();
        return;
      }
    }

    setQuestionSet(questionSet + 1);
  };

  //Handle back button
  const handleBackClick = (event) => {
    event.preventDefault();
    if (questionSet > 1) {
      setQuestionSet(questionSet - 1);
      // Reset phone auth states when going back from the final screen
      if (questionSet === 3) {
        setCodeSent(false);
        setVerificationCode("");
        setConfirmObj(null);
        setIsRequestButtonDisabled(false);
        setIsVerifyButtonDisabled(false);
        clearRecaptchaVerifier("recaptcha-container");
      }
    }
  };

  return (
    <div className="LoginContainer">
      <div className="regLefts">
        <img
          src={logo}
          alt="Logo"
          style={{ width: "200px", marginBottom: "20px" }}
        />
        <p style={styles.content}>
          Welcome to Ezwinds Digital Software Platform for Stores
        </p>
        {questionSet === 1 ? (
          <div style={styles.progressContainer}>
            <span style={styles.progressDotActive}></span>
            <span style={styles.progressDot}></span>
            <span style={styles.progressDot}></span>
          </div>
        ) : questionSet === 2 ? (
          <div style={styles.progressContainer}>
            <span style={styles.progressDotActive}></span>
            <span style={styles.progressDotActive}></span>
            <span style={styles.progressDot}></span>
          </div>
        ) : (
          <div style={styles.progressContainer}>
            <span style={styles.progressDotActive}></span>
            <span style={styles.progressDotActive}></span>
            <span style={styles.progressDotActive}></span>
          </div>
        )}
      </div>

      <div className="RightsPanels">
        {questionSet === 1 && (
          <h2 style={styles.formTitle}>
            Let us learn more about your business!
          </h2>
        )}

        {questionSet === 2 && (
          <h2 style={styles.formTitle}>Let's set up your account details!</h2>
        )}

        {/* Step 3 title placeholder - currently Step 2 handles verification */}
        {questionSet === 3 && (
          <h2 style={styles.formTitle}>Verify your mobile number</h2>
        )}

        <div className="formContainer whiteBox-form">
          <form onSubmit={handleNextClick} style={styles.form}>
            {questionSet === 1 ? (
              <div>
                <label style={styles.label}>Business Name:</label>
                <input
                  type="text"
                  placeholder="Business Name"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  style={styles.input}
                  required
                />

                <label style={styles.label}>Business Address:</label>
                <input
                  type="text"
                  placeholder="Business Address"
                  value={businessAddress}
                  onChange={(e) => setBusinessAddress(e.target.value)}
                  style={styles.input}
                  required
                />

                <div className="btnAlign">
                  <button type="submit" style={styles.button}>
                    Next
                  </button>
                </div>
              </div>
            ) : (
              questionSet === 2 && (
                <div>
                  {/* Inputs for First Name, Last Name, etc. */}
                  <label style={styles.label}>First Name:</label>
                  <input
                    type="text"
                    placeholder="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    style={styles.input}
                    required
                  />

                  <label style={styles.label}>Last Name:</label>
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    style={styles.input}
                    required
                  />

                  <label style={styles.label}>Email:</label>
                  <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={styles.input}
                    required
                  />

                  <label style={styles.label}>Username:</label>
                  <input
                    type="text"
                    placeholder="Username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    onBlur={(e) => checkUsernameAvailability(e.target.value)}
                    style={styles.input}
                    required
                  />
                  <label style={styles.label}>Password:</label>
                  <input
                    type="password" // Use type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={styles.input}
                    required
                  />

                  <label style={styles.label}>
                    Phone Number to Get Code: *
                  </label>
                  <div style={styles.inputGroup}>
                    <select
                      value={selectedCountryCode}
                      onChange={handleCountryCodeChange}
                      style={{ ...styles.input, ...styles.select }}
                      disabled={codeSent}
                    >
                      {countryCodes.map((country) => (
                        <option key={country.code} value={country.dial_code}>
                          {country.dial_code}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      placeholder="Enter phone number"
                      value={phoneNumber}
                      onChange={handlePhoneNumberChange}
                      style={{ ...styles.input, borderRadius: "0 5px 5px 0" }}
                      disabled={codeSent}
                      required
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <input
                      type="checkbox"
                      id="sms-opt-in"
                      checked={isSmsEnabled}
                      onChange={handleCheckboxChange}
                      style={{
                        width: "18px",
                        height: "18px",
                        cursor: "pointer",
                      }}
                    />
                    <label
                      htmlFor="sms-opt-in"
                      style={{
                        cursor: "pointer",
                        fontSize: "14px",
                        fontFamily: "sans-serif",
                      }}
                    >
                      Opt-in for SMS
                    </label>
                  </div>

                  <label className="flex items-start gap-2.5 mt-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreedToMSA}
                      onChange={(e) => setAgreedToMSA(e.target.checked)}
                      className="mt-0.5 w-4 h-4 flex-shrink-0 accent-blue-600"
                      required
                    />
                    <span className="text-xs text-gray-600 leading-relaxed">
                      I agree to the{" "}
                      <a
                        href={MSA_PATH}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 font-medium underline"
                      >
                        Master Service Agreement
                      </a>
                    </span>
                  </label>

                  <div
                    className="btnAlign"
                    style={{
                      display: "flex",
                      gap: "10px",
                      flexDirection: window.innerWidth < 600 ? "column" : "row",
                    }}
                  >
                    <button
                      type="button"
                      onClick={handleBackClick}
                      style={styles.button}
                      disabled={isSigningIn || isVerifyingCode}
                    >
                      Back
                    </button>

                    {!codeSent && (
                      <button
                        type="button"
                        onClick={sendVerificationCode}
                        style={{
                          ...styles.button,
                          opacity:
                            isRequestButtonDisabled ||
                            isSigningIn ||
                            !isPhoneNumberValid()
                              ? 0.6
                              : 1,
                        }}
                        disabled={
                          isRequestButtonDisabled ||
                          isSigningIn ||
                          !isPhoneNumberValid()
                        }
                      >
                        {isSigningIn
                          ? "Sending..."
                          : "Request Code for Account Creation"}
                      </button>
                    )}
                  </div>

                  {/* <div className="btnAlign" style={{ display: "flex", gap: "10px" }}>
                    <button type="button" onClick={handleBackClick} style={styles.button} disabled={isSigningIn || isVerifyingCode}>
                      Back
                    </button>
                    {!codeSent && (
                        <button type="button" onClick={sendVerificationCode} style={{
                          ...styles.button,
                          opacity: (isRequestButtonDisabled || isSigningIn || !isPhoneNumberValid()) ? 0.6 : 1
                        }} disabled={isRequestButtonDisabled || isSigningIn || !isPhoneNumberValid()}>

                          {isSigningIn ? 'Sending...' : 'Request Code for Account Creation'}
                        </button>
                    )}
                  </div> */}

                  {codeSent && (
                    <>
                      <label style={styles.label}>
                        Enter Verification Code:
                      </label>
                      <input
                        type="text"
                        placeholder="Verification Code"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        style={styles.input}
                        required
                      />
                      <button
                        onClick={verifyCodeAndCreateAccount}
                        style={{ ...styles.button, width: "100%" }}
                        disabled={isVerifyButtonDisabled || isVerifyingCode}
                      >
                        {isVerifyingCode
                          ? "Verifying..."
                          : "Verify and Create Account"}
                      </button>
                      {error && (
                        <div style={{ color: "red", marginTop: "10px" }}>
                          {error}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )
            )}
            {/* Add a hidden div for the reCAPTCHA verifier */}

            <div id="recaptcha-container" style={{ minHeight: "60px" }}></div>
          </form>
        </div>
      </div>
    </div>
  );
};

const styles = {
  inputGroup: {
    display: "flex",
    width: "100%",
    marginBottom: "10px",
  },
  select: {
    flex: "none",
    border: "1px solid #ccc",
    padding: "10px",
    borderTopLeftRadius: "5px",
    borderBottomLeftRadius: "5px",
    marginRight: "2px",
    width: "auto",
    borderRadius: "5px 0 0 5px", // Ensure only left sides are rounded
  },

  title: {
    marginBottom: "20px",
  },
  content: {
    textAlign: "center",
    marginBottom: "20px", // Add margin for spacing before progress dots
  },
  progressContainer: {
    display: "flex",
    justifyContent: "center",
  },
  progressDot: {
    height: "10px",
    width: "10px",
    backgroundColor: "grey",
    borderRadius: "50%",
    margin: "5px",
  },
  progressDotActive: {
    height: "10px",
    width: "10px",
    backgroundColor: "#66EE77", // Active dot color
    borderRadius: "50%",
    margin: "5px",
  },
  infoBox: {
    backgroundColor: "white",
    padding: "20px",
    borderRadius: "10px",
    textAlign: "center",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
  list: {
    listStyleType: "none",
    padding: 0,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    width: "100%", // Adjust as needed
    alignItems: "flex-start", // Align children to the start
  },
  formTitle: {
    color: "#227722",
    marginBottom: "20px",
    marginTop: "2px",
    justifyContent: "center",
    display: "flex",
    textAlign: "center",
  },
  input: {
    margin: "5px 0 10px",
    padding: "10px",
    borderRadius: "5px",
    border: "1px solid #ccc",
    width: "100%",
  },
  button: {
    backgroundColor: "#338833",
    color: "white",
    border: "none",
    padding: "10px 35px",
    borderRadius: "5px",
    marginTop: "10px",
    cursor: "pointer",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
  },
  label: {
    textAlign: "left", // Align text to the left
    width: "100%", // Ensure label takes full width
    margin: "10px 0px 0",
    padding: "0",
  },
};

export default RegistrationScreen;
